import { salesTileKeyType } from 'components/pages/GrowthAnalysis';
import { colorConstants } from 'config/colorConstants';
import { ConfigKeys } from 'utils/Enums';
import {
    fetchCurrency,
    formatNumWithKMBAlt,
    getAbsoluteChange,
    getConfig,
    getPercentageChangeV2,
    rateOptimalIndex,
} from 'utils/helperMethods';

type chartDataType = {
    data: any[];
    investmentMinVal: number;
    investmentMaxVal: number;
    roiMinVal: number;
    roiMaxVal: number;
};

type ChartResultDataType = {
    chartData: chartDataType;
};

type TableResultDataType = {
    tableData: any;
};

export function promoLandscapeChartParser(data: any[], decimalForNonPercentage: number) {
    const result: ChartResultDataType = {
        chartData: { data: [], investmentMinVal: 0, investmentMaxVal: 0, roiMinVal: 0, roiMaxVal: 0 },
    };
    try {
        const parsedChartData: any[] = [];
        const investmentKey = 'investments';
        const roiKey = 'roi';
        const additionalData = 'additional_data';
        data.forEach((item, index) => {
            const cyData = dataCrawler(item.attribute_data, 0, investmentKey);
            const yaData = dataCrawler(item.attribute_data, 1, investmentKey);
            const roiCyData = dataCrawler(item.attribute_data, 0, roiKey);
            const roiYaData = dataCrawler(item.attribute_data, 1, roiKey);
            let cyAdditionalData = dataCrawler(item.attribute_data, 0, additionalData) || {};
            let yaAdditionalData = dataCrawler(item.attribute_data, 1, additionalData) || {};

            const investmentAbsChange = getAbsoluteChange(yaData, cyData);
            const investmentPercentageChange = getPercentageChangeV2(yaData, cyData);
            const roiAbsChange = getAbsoluteChange(yaData, cyData);
            const roiPercentageChange = getPercentageChangeV2(yaData, cyData);

            cyAdditionalData = Object.keys(cyAdditionalData).map((key: string) => {
                return {
                    name: key,
                    value: formatNumWithKMBAlt(cyAdditionalData[key], decimalForNonPercentage, decimalForNonPercentage),
                };
            });
            yaAdditionalData = Object.keys(yaAdditionalData).map((key: string) => {
                return {
                    name: key,
                    value: formatNumWithKMBAlt(yaAdditionalData[key], decimalForNonPercentage, decimalForNonPercentage),
                };
            });
            result.chartData.investmentMinVal = Math.min(
                index === 0 ? cyData : result.chartData.investmentMinVal,
                cyData,
                yaData,
            );
            result.chartData.investmentMaxVal = Math.max(
                index === 0 ? cyData : result.chartData.investmentMaxVal,
                cyData,
                yaData,
            );
            result.chartData.roiMinVal = Math.min(
                index === 0 ? roiCyData : result.chartData.roiMinVal,
                roiCyData,
                roiYaData,
            );
            result.chartData.roiMaxVal = Math.max(
                index === 0 ? roiCyData : result.chartData.roiMaxVal,
                roiCyData,
                roiYaData,
            );
            //CY  DATA
            const lag0TempObj = {
                category: item.attribute_name,
                sales: cyData,
                type: 'CY',
                roi: roiCyData,
                color: colorConstants.cyan,
                extraItems: cyAdditionalData,
                extras: {
                    investmentAbsChange,
                    investmentPercentageChange,
                    roiAbsChange,
                    roiPercentageChange,
                },
            };

            //YA  DATA
            const lag1TempObj = {
                category: item.attribute_name,
                sales: yaData,
                type: 'YA',
                roi: roiYaData,
                extraItems: yaAdditionalData,
                color: colorConstants.cyan,
                extras: {
                    investmentAbsChange,
                    investmentPercentageChange,
                    roiAbsChange,
                    roiPercentageChange,
                },
            };

            parsedChartData.push(lag1TempObj, lag0TempObj);
        });
        result.chartData.data = parsedChartData;
        return result;
    } catch (err) {
        return result;
    }
}
export function promoLandscapeTableParser(data: any[], promoLandscapeConfig: any, config: any) {
    const currency = fetchCurrency(config);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, config);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;
    const tableRowMapping = promoLandscapeConfig?.tableRowsKeyMapping;
    const result: TableResultDataType = {
        tableData: {},
    };
    const optimalIndexKey = promoLandscapeConfig?.optimalIndexKey;
    const attributeDataKey = 'attribute_data';
    try {
        const tableData: any[] = [];
        tableRowMapping.forEach((header: any) => {
            let tempObj: any = {
                label: header.label,
            };
            data.forEach((item: any) => {
                const pre = header.unit === '%' ? '' : header.unit;
                const post = header.unit === '%' ? header.unit : '';
                const decimalFormatter = post === '%' ? decimalForPercentage : decimalForNonPercentage;
                //CY DATA,YA DATA, CHANGE DATA
                tempObj[`${item['attribute_name']} `] = {
                    value:
                        header.key === optimalIndexKey
                            ? rateOptimalIndex(
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold1,
                                  header?.parentKey
                                      ? item[attributeDataKey][0][header.parentKey][header.key]
                                      : item[attributeDataKey][0][header.key],
                                  promoLandscapeConfig.thresholdColorMapping,
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold2,
                              )
                            : header?.parentKey
                            ? pre +
                              formatNumWithKMBAlt(
                                  item[attributeDataKey][0][header.parentKey][header.key],
                                  decimalFormatter,
                                  decimalFormatter,
                              ) +
                              post
                            : pre + formatNumWithKMBAlt(item[attributeDataKey][0][header.key], 2, 2) + post,
                    cy:
                        header.key === optimalIndexKey
                            ? rateOptimalIndex(
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold1,
                                  header?.parentKey
                                      ? item[attributeDataKey][0][header.parentKey][header.key]
                                      : item[attributeDataKey][0][header.key],
                                  promoLandscapeConfig.thresholdColorMapping,
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold2,
                              )
                            : header?.parentKey
                            ? pre +
                              formatNumWithKMBAlt(
                                  item[attributeDataKey][0][header.parentKey][header.key],
                                  decimalFormatter,
                                  decimalFormatter,
                              ) +
                              post
                            : pre +
                              formatNumWithKMBAlt(
                                  item[attributeDataKey][0][header.key],
                                  decimalFormatter,
                                  decimalFormatter,
                              ) +
                              post,
                    ya:
                        header.key === optimalIndexKey
                            ? rateOptimalIndex(
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold1,
                                  header?.parentKey
                                      ? item[attributeDataKey][1][header.parentKey][header.key]
                                      : item[attributeDataKey][1][header.key],
                                  promoLandscapeConfig.thresholdColorMapping,
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold2,
                              )
                            : header?.parentKey
                            ? pre +
                              formatNumWithKMBAlt(
                                  item[attributeDataKey][1][header.parentKey][header.key],
                                  decimalFormatter,
                                  decimalFormatter,
                              ) +
                              post
                            : pre +
                              formatNumWithKMBAlt(
                                  item[attributeDataKey][1][header.key],
                                  decimalFormatter,
                                  decimalFormatter,
                              ) +
                              post,
                    absChange:
                        header.key === optimalIndexKey
                            ? rateOptimalIndex(
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold1,
                                  header?.parentKey
                                      ? item[attributeDataKey][0][header.parentKey][header.key]
                                      : item[attributeDataKey][0][header.key],
                                  promoLandscapeConfig.thresholdColorMapping,
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold2,
                              )
                            : pre +
                              formatNumWithKMBAlt(
                                  getAbsoluteChange(
                                      header?.parentKey
                                          ? item[attributeDataKey][1][header.parentKey][header.key]
                                          : item[attributeDataKey][1][header.key],
                                      header?.parentKey
                                          ? item[attributeDataKey][0][header.parentKey][header.key]
                                          : item[attributeDataKey][0][header.key],
                                  ),
                                  decimalFormatter,
                                  decimalFormatter,
                              ) +
                              post,
                    percentageChange:
                        header.key === optimalIndexKey
                            ? rateOptimalIndex(
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold1,
                                  header?.parentKey
                                      ? item[attributeDataKey][0][header.parentKey][header.key]
                                      : item[attributeDataKey][0][header.key],
                                  promoLandscapeConfig.thresholdColorMapping,
                                  promoLandscapeConfig.optimalInvestmentIndexThreshold2,
                              )
                            : `${formatNumWithKMBAlt(
                                  getPercentageChangeV2(
                                      header?.parentKey
                                          ? item[attributeDataKey][1][header.parentKey][header.key]
                                          : item[attributeDataKey][1][header.key],
                                      header?.parentKey
                                          ? item[attributeDataKey][0][header.parentKey][header.key]
                                          : item[attributeDataKey][0][header.key],
                                  ),
                                  decimalFormatter,
                                  decimalFormatter,
                              )}%`,
                };
            });
            tempObj.isExpandable = Boolean(header.parentKey);
            tempObj.nestedIndex = null;
            tableData.push(tempObj);

            const indexVal = tableData.length;
            if (header?.children) {
                header.children.forEach((nestedHeader: any, index: number) => {
                    let nestedObj: any = {
                        label: nestedHeader.label,
                    };
                    const pre = nestedHeader.unit === '%' ? '' : nestedHeader.unit;
                    const post = nestedHeader.unit === '%' ? nestedHeader.unit : '';
                    const decimalFormatter = post === '%' ? decimalForPercentage : decimalForNonPercentage;

                    data.forEach((item: any) => {
                        //CY DATA,YA DATA, CHANGE DATA
                        nestedObj[item['attribute_name'] + ' '] = {
                            value:
                                pre +
                                formatNumWithKMBAlt(
                                    item[attributeDataKey][0][header.parentKey][nestedHeader.key],
                                    decimalFormatter,
                                    decimalFormatter,
                                ) +
                                post,
                            cy:
                                pre +
                                formatNumWithKMBAlt(
                                    item[attributeDataKey][0][header.parentKey][nestedHeader.key],
                                    decimalFormatter,
                                    decimalFormatter,
                                ) +
                                post,
                            ya:
                                pre +
                                formatNumWithKMBAlt(
                                    item[attributeDataKey][1][header.parentKey][nestedHeader.key],
                                    decimalFormatter,
                                    decimalFormatter,
                                ) +
                                post,
                            absChange:
                                formatNumWithKMBAlt(
                                    getAbsoluteChange(
                                        item[attributeDataKey][1][header.parentKey][nestedHeader.key],
                                        item[attributeDataKey][0][header.parentKey][nestedHeader.key],
                                    ),
                                    decimalFormatter,
                                    decimalFormatter,
                                ) + post,
                            percentageChange: `${formatNumWithKMBAlt(
                                getPercentageChangeV2(
                                    item[attributeDataKey][1][header.parentKey][nestedHeader.key],
                                    item[attributeDataKey][0][header.parentKey][nestedHeader.key],
                                ),
                                decimalFormatter,
                                decimalFormatter,
                            )}%`,
                        };
                    });
                    nestedObj.isExpandable = false;
                    nestedObj.nestedIndex = indexVal - 1;
                    tableData.push(nestedObj);
                });
            }
        });
        //   console.log(tableData, 'logging data');
        result.tableData = tableData;
        return result;
    } catch (err) {
        console.log('logging error', err);
        return result;
    }
}
function dataCrawler(array: any[], index: number, key: string) {
    return array[index][key] || 0;
}
